import { Code } from "@phosphor-icons/react";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { NotFoundError } from "~/api/errors";
import { useWisdomNode } from "~/api/queries";

/**
 * "Describes code" — the code this document is bridged to (CWAY): the components and files the
 * bridge ties it to, each linking to its row in the project's top-down view
 * (`/knowledge/project?component=`).
 *
 * Quiet when the document describes nothing and when the graph holds no such document — most
 * documents are not about code, and a permanent empty box above every one of them would be noise.
 * A FAILED read is not quiet: it says so, because an absent panel and a broken one must differ.
 * What the caller may not read is counted, never listed (the server withholds it).
 */
export function DescribesCodePanel({ documentId }: { documentId: string }) {
  const { t } = useTranslation();
  const node = useWisdomNode("document", documentId);

  if (node.isLoading) {
    return null;
  }

  // "Not in the graph yet" (a 404 — the server answers it identically for a document that never
  // projected and one the caller may not read) is a normal state, not a failure.
  if (node.error && !(node.error instanceof NotFoundError)) {
    return (
      <p className="px-4 text-small text-text-tertiary" data-testid="describes-code-error">
        {t("What code this document describes could not be read just now.")}
      </p>
    );
  }

  const group = node.data?.edges.find((g) => g.rel === "DESCRIBES");
  const targets = (group?.returned ?? []).filter(
    (e) => e.direction === "out" && (e.kind === "codeComponent" || e.kind === "codeFile"),
  );
  const hidden = group?.withheld ?? 0;
  if (targets.length === 0 && hidden === 0) {
    return null;
  }

  return (
    <section
      aria-label={t("Describes code")}
      data-testid="describes-code"
      className="flex flex-wrap items-center gap-2 px-4 py-2 text-small"
    >
      <span className="flex items-center gap-1 font-medium text-text">
        <Code size={14} aria-hidden />
        {t("Describes code")}
      </span>
      {targets.map((e) =>
        e.kind === "codeComponent" ? (
          <Link
            key={e.id}
            to={"/knowledge/project" as never}
            search={{ component: e.title } as never}
            data-testid="describes-code-chip"
            className="rounded-sm border border-border px-2 py-0.5 text-text hover:bg-bg-secondary"
          >
            {e.title}
          </Link>
        ) : (
          <span
            key={e.id}
            data-testid="describes-code-chip"
            className="rounded-sm border border-border px-2 py-0.5 text-text-secondary"
          >
            {e.title}
          </span>
        ),
      )}
      {group && group.totalCount > targets.length + hidden ? (
        <span className="text-text-tertiary">
          {t("{{n}} more not shown", { n: group.totalCount - targets.length - hidden })}
        </span>
      ) : null}
      {hidden > 0 ? (
        <span className="text-text-tertiary" data-testid="describes-code-withheld">
          {t("{{n}} you cannot see", { n: hidden })}
        </span>
      ) : null}
    </section>
  );
}
